import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { firstValueFrom } from 'rxjs';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as passengerSlice from '../../../data/passenger.slice';
import { Passenger } from '../../../data/models/passenger.model';
import TextInput from '../../forms/TextInput';
import AddressInput from '../../forms/AddressInput';
import triplegService from '../../../services/triplegService';
import type { AddressValidationRequest } from '../../../data/requests/address-validation';
import { useFeatureAddressValidationForCurrentPortal } from '../../../hooks/features/useFeatureAddressValidation';
import {
  tripLegConfidenceErrorTranslationKey,
  type TripLegConfidenceErrorPayload,
} from '../../reservations/machines/tripLegConfidence';

const PREFIX = 'PassengerNamedLocationsDialog';

const classes = {
  content: `${PREFIX}-content`,
  message: `${PREFIX}-message`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    width: 500,
    gap: theme.spacing(2),
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },
}));

interface PassengerNamedLocationsDialogProps {
  passenger: Passenger | null;
  open: boolean;
  close: () => void;
}

export default function PassengerNamedLocationsDialog({
  passenger,
  open,
  close,
}: PassengerNamedLocationsDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [error, setError] = React.useState('');
  const requiredFieldMessage = t('common.requiredField');
  const passengerUUID = passenger?.passengerUUID || '';

  const addressValidationEnabled =
    useFeatureAddressValidationForCurrentPortal();

  const [validatingAddress, setValidatingAddress] = React.useState(false);
  // The deterministic error /locations/validate rejected the pick with. The
  // translated message is derived at render time (below) so the validation
  // effect doesn't depend on the `t` identity — re-running it would abort the
  // in-flight call. NOTE: the endpoint returns the confidence score as data
  // and never gates on it; by design the FE doesn't either (no low-confidence
  // error/warning here) — if a score should ever block, the API is the place
  // to enforce it.
  const [addressValidationServerError, setAddressValidationServerError] =
    React.useState<TripLegConfidenceErrorPayload | null>(null);

  const clearAddressValidationState = () => {
    setValidatingAddress(false);
    setAddressValidationServerError(null);
  };

  // Rendered red and gating OK.
  const addressValidationError = React.useMemo(() => {
    if (!addressValidationServerError) return '';
    const translationKey = tripLegConfidenceErrorTranslationKey(
      addressValidationServerError,
    );
    return (
      (translationKey
        ? t(translationKey, {
            ...(addressValidationServerError.errorCodeData ?? {}),
            defaultValue: addressValidationServerError.message ?? '',
          })
        : addressValidationServerError.message) || t('common.invalidAddress')
    );
  }, [addressValidationServerError, t]);

  const formHook = useForm({
    defaultValues: {
      passengerAddressUseBC: 'RESERVATION',
    },
  });

  const { watch, setValue, control } = formHook;

  React.useEffect(() => {
    if (!open || !addressValidationEnabled) {
      // Once inactive, the watch subscription below is gone, so nothing else
      // would ever clear a leftover error / in-flight flag (a stale red error
      // would gate OK with no way to dismiss it). Clearing here also covers a
      // close that bypasses handleClose (the parent flipping `open` directly).
      clearAddressValidationState();
      return;
    }

    // putJSON takes no AbortSignal (same as /score/trip-leg), so the controller
    // is a stale-result token: a newer pick or dialog close marks the in-flight
    // run aborted and its resolution is ignored.
    let abortController: AbortController | undefined;

    const subscription = formHook.watch((value, { name }) => {
      // AddressInput writes `addressStruct` exactly once per completed
      // autocomplete pick (or '' on clear/failed details fetch), so validation
      // fires per pick — no debounce needed, unlike the keystroke-driven
      // reservation forms. The field is set by AddressInput rather than
      // declared in defaultValues, so it's absent from the inferred form type.
      if ((name as string) !== 'addressStruct') return;

      abortController?.abort();

      const addressStruct = (value as { addressStruct?: string })
        ?.addressStruct;
      let address: Record<string, string> | undefined;
      try {
        address = addressStruct ? JSON.parse(addressStruct) : undefined;
      } catch {
        address = undefined;
      }
      // No struct (cleared field / failed details fetch) or no addressLine1
      // (AddressInput already flags that pick invalid itself) → nothing to
      // validate; drop any result from a previous pick.
      if (!address?.addressLine1?.trim()) {
        clearAddressValidationState();
        return;
      }

      const controller = new AbortController();
      abortController = controller;

      // Address fields only, mirroring the trip-leg score payload. The city/
      // stateCode/zipCode schema fields reject empty strings (min 1), so empty
      // values are omitted rather than sent as ''.
      const payload: AddressValidationRequest = {
        addressLine1: address.addressLine1,
      };
      if (address.addressLine2) payload.addressLine2 = address.addressLine2;
      if (address.city) payload.city = address.city;
      if (address.stateCode) payload.stateCode = address.stateCode;
      if (address.zipCode) payload.zipCode = address.zipCode;

      setValidatingAddress(true);
      setAddressValidationServerError(null);
      firstValueFrom(triplegService.validateAddress(payload))
        .then(() => {
          // The confidence score in the response is deliberately ignored —
          // only endpoint errors surface (see the state's NOTE).
          if (controller.signal.aborted) return;
          setValidatingAddress(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setValidatingAddress(false);
          const validationError: TripLegConfidenceErrorPayload =
            err && typeof err === 'object'
              ? (err as TripLegConfidenceErrorPayload)
              : { message: String(err) };
          // Transient/network failures (no errorCode) never block the save —
          // the backend write is the final gate. Only deterministic 4xx errors
          // (invalid address — a 404 since transport-api 1.3.2) do.
          setAddressValidationServerError(
            validationError.errorCode ? validationError : null,
          );
        });
    });

    return () => {
      abortController?.abort();
      subscription.unsubscribe();
    };
  }, [open, formHook.watch, addressValidationEnabled]);

  const handleClose = () => {
    formHook.reset();
    clearAddressValidationState();
    close();
  };
  const handleSave = (data: any) => {
    if (data.addressStruct) {
      const addressStruct = JSON.parse(data.addressStruct);
      const { addressLine1, city, stateCode } = addressStruct;

      const isExist = passenger?.passengerAddresses?.some(
        (address) =>
          address.addressLine1 === addressLine1 &&
          address.city === city &&
          address.stateCode === stateCode,
      );

      if (!isExist) {
        const { passengerAddressUseBC, label } = data;
        dispatch(
          passengerSlice.addPassengerReservationAddressAsync(
            passengerUUID,
            addressStruct,
            passengerAddressUseBC,
            label,
          ),
        );
        formHook.reset();
        setError('');
        clearAddressValidationState();
        close();
      } else {
        setError(t('snackbar.addresssAlreadyExist'));
      }
    }
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>{t('passenger.addNamedLocation')}</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <AddressInput
            name="address"
            label={t('passenger.namedLocations')}
            required={true}
            watch={watch}
            setValue={setValue}
            errorMessage={addressValidationError || error}
          />
          <TextInput
            name="label"
            label={t('passenger.label')}
            control={control}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !formHook.formState.isValid ||
            validatingAddress ||
            !!addressValidationError
          }
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
